import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { supabase, getApiBaseUrl } from '../../services/api';
import { locationTypes, locationNames, getStateAbbreviation } from '../../constants/locations';
import './Modals.css';
import './DefaultLocationModal.css';

const DefaultLocationModal = ({ onComplete, prefilledLocation, onConsumed, onDefaultLocationChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isNavigatingWithKeyboard, setIsNavigatingWithKeyboard] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(480);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 100);

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setIsNavigatingWithKeyboard(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdownTop = inputRect.bottom + 4;
      const available = window.innerHeight * 0.95 - dropdownTop;
      setDropdownMaxHeight(Math.max(120, available));
    }
  }, [showDropdown, searchResults]);

  const normalizeName = (name) => {
    return name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/-/g, ' ')
      .replace(/[.(),\-']/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatLocationName = (result) => {
    if (!result?.name) return '';

    if ((result.type === 'county' || result.type === 'city') && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const local = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${local}, ${stateAbbr}`;
    }

    return result.name;
  };

  useEffect(() => {
    if (prefilledLocation?.name && prefilledLocation?.type) {
      setSelectedLocation(prefilledLocation);
      setInputValue(formatLocationName(prefilledLocation));
      onConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processLocationResults = (locations, type, query) => {
    const normalizedQuery = normalizeName(query);
    const trimmedQuery = query.trim();

    if (normalizedQuery.length <= 2) {
      const matches = locations
        .filter(name => normalizeName(name).includes(normalizedQuery))
        .map(name => ({
          name,
          type,
          isExactMatch: normalizeName(name).startsWith(normalizedQuery)
        }))
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          return 0;
        });

      return matches.slice(0, 5);
    }

    if ((type === 'county' || type === 'city') && normalizedQuery.length > 0) {
      const startsWithMatches = [];
      const containsMatches = [];
      const hasSpace = trimmedQuery.includes(' ');

      locations.forEach(fullName => {
        if (fullName.includes(', ')) {
          const [localName, stateName] = fullName.split(', ');
          const normalizedLocalName = normalizeName(localName);
          const normalizedStateName = normalizeName(stateName);
          const stateAbbr = getStateAbbreviation(stateName);
          const normalizedStateAbbr = normalizeName(stateAbbr);

          let matches = false;
          let startsWithCity = false;
          let exactCityMatch = false;

          if (hasSpace) {
            const lastSpaceIndex = trimmedQuery.lastIndexOf(' ');
            const cityPart = trimmedQuery.substring(0, lastSpaceIndex).trim();
            const statePart = trimmedQuery.substring(lastSpaceIndex + 1).trim();
            const normalizedCityPart = normalizeName(cityPart);
            const normalizedStatePart = normalizeName(statePart);

            const words = trimmedQuery.split(/\s+/);
            let altCityPart = '';
            let altStatePart = '';
            if (words.length >= 3) {
              altCityPart = words.slice(0, -2).join(' ');
              altStatePart = words.slice(-2).join(' ');
            }
            const normalizedAltCityPart = normalizeName(altCityPart);
            const normalizedAltStatePart = normalizeName(altStatePart);

            const exactMatch = normalizedLocalName.includes(normalizeName(trimmedQuery));
            const cityAndStateMatch1 = statePart && normalizedLocalName.includes(normalizedCityPart) &&
              (normalizedStateName.startsWith(normalizedStatePart) || normalizedStateAbbr.startsWith(normalizedStatePart));
            const cityAndStateMatch2 = altCityPart && normalizedLocalName.includes(normalizedAltCityPart) &&
              (normalizedStateName.startsWith(normalizedAltStatePart) || normalizedStateAbbr.startsWith(normalizedAltStatePart));

            if (exactMatch || cityAndStateMatch1 || cityAndStateMatch2) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizeName(trimmedQuery)) ||
                normalizedLocalName.startsWith(normalizedCityPart) ||
                (altCityPart && normalizedLocalName.startsWith(normalizedAltCityPart));
              exactCityMatch = normalizedLocalName === normalizeName(trimmedQuery) ||
                normalizedLocalName === normalizedCityPart ||
                (altCityPart && normalizedLocalName === normalizedAltCityPart);
            }
          } else {
            if (normalizedLocalName.includes(normalizedQuery) ||
              normalizedStateName.includes(normalizedQuery) ||
              normalizedStateAbbr.includes(normalizedQuery)) {
              matches = true;
              startsWithCity = normalizedLocalName.startsWith(normalizedQuery);
              exactCityMatch = normalizedLocalName.startsWith(normalizedQuery);
            }
          }

          if (matches) {
            const matchData = {
              name: fullName,
              localName,
              normalizedLocalName,
              type,
              isExactMatch: exactCityMatch,
              matchType: startsWithCity ? 'starts_with' : 'contains'
            };

            if (startsWithCity) {
              startsWithMatches.push(matchData);
            } else {
              containsMatches.push(matchData);
            }
          }
        }
      });

      const allMatches = [...startsWithMatches, ...containsMatches];
      const sortedMatches = allMatches.sort((a, b) => {
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
        if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
        return a.localName.localeCompare(b.localName);
      });

      const exactMatches = sortedMatches.filter(m => m.isExactMatch);
      if (exactMatches.length >= 5) return exactMatches;
      return sortedMatches.slice(0, 5);
    }

    const startMatches = locations
      .filter(name => normalizeName(name).startsWith(normalizedQuery))
      .map(name => ({ name, type, isExactMatch: true, matchType: 'starts_with' }));

    const containsMatches = locations
      .filter(name => {
        const normalizedName = normalizeName(name);
        return normalizedName.includes(normalizedQuery) && !normalizedName.startsWith(normalizedQuery);
      })
      .map(name => ({ name, type, isExactMatch: false, matchType: 'contains' }));

    const allMatches = [...startMatches, ...containsMatches].sort((a, b) => {
      if (a.isExactMatch && !b.isExactMatch) return -1;
      if (!a.isExactMatch && b.isExactMatch) return 1;
      if (a.matchType === 'starts_with' && b.matchType === 'contains') return -1;
      if (a.matchType === 'contains' && b.matchType === 'starts_with') return 1;
      return 0;
    });

    const exactMatches = allMatches.filter(m => m.isExactMatch);
    if (exactMatches.length >= 5) return exactMatches;
    return allMatches.slice(0, 5);
  };

  const getFlattenedResults = useCallback(() => {
    const results = [];
    [...locationTypes].reverse().forEach(locationType => {
      const typeResults = searchResults.filter(result => result.type === locationType.value);
      results.push(...typeResults);
    });
    return results;
  }, [searchResults]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setSelectedLocation(null);
    setIsNavigatingWithKeyboard(false);
    setError(null);

    if (query.length > 0) {
      const lowercaseQuery = query.toLowerCase();

      const metroLetters = ['m', 'me', 'met', 'metr', 'metro'];
      const words = lowercaseQuery.split(/\s+/);
      const hasMetroWord = words.some(word => metroLetters.includes(word));
      const queryWithoutMetro = hasMetroWord
        ? lowercaseQuery.replace(/\b(m|me|met|metr|metro)\b/g, '').trim()
        : lowercaseQuery;

      const cityResults = processLocationResults(locationNames.City, 'city', lowercaseQuery);
      const countyResults = processLocationResults(locationNames.County, 'county', lowercaseQuery);
      const metroResults = processLocationResults(locationNames.Metro, 'metro', hasMetroWord ? queryWithoutMetro : lowercaseQuery);
      const stateResults = processLocationResults(locationNames.State, 'state', lowercaseQuery);
      const divisionResults = processLocationResults(locationNames.Division, 'division', lowercaseQuery);
      const regionResults = processLocationResults(locationNames.Region, 'region', lowercaseQuery);
      const nationResults = processLocationResults(locationNames.Nation, 'nation', lowercaseQuery);

      const results = [
        ...cityResults, ...countyResults, ...metroResults,
        ...stateResults, ...divisionResults, ...regionResults, ...nationResults
      ];

      setSearchResults(results);
      setShowDropdown(results.length > 0);
      setHighlightedIndex(-1);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setInputValue(selectedLocation ? formatLocationName(selectedLocation) : '');
      setSearchResults([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      setIsNavigatingWithKeyboard(false);
      return;
    }

    if (e.key === 'Enter' && selectedLocation && !showDropdown) {
      e.preventDefault();
      handleConfirm();
      return;
    }

    if (!showDropdown || searchResults.length === 0) return;

    const flatResults = getFlattenedResults();
    const maxIndex = flatResults.length - 1;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        setIsNavigatingWithKeyboard(true);
        setHighlightedIndex(prev => {
          const nextIndex = e.key === 'ArrowDown'
            ? (prev < maxIndex ? prev + 1 : 0)
            : (prev > 0 ? prev - 1 : maxIndex);
          scrollToHighlighted(nextIndex);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults.length > 0) {
          const index = highlightedIndex === -1 ? 0 : highlightedIndex;
          handleLocationSelect(flatResults[index]);
        }
        break;
      default:
        setIsNavigatingWithKeyboard(false);
        break;
    }
  };

  const scrollToHighlighted = (index) => {
    if (dropdownRef.current) {
      const element = dropdownRef.current.querySelector(`[data-index="${index}"]`);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };

  const handleLocationSelect = (result) => {
    if (!result) return;
    setSelectedLocation(result);
    setInputValue(formatLocationName(result));
    setShowDropdown(false);
    setSearchResults([]);
    setHighlightedIndex(-1);
    setIsNavigatingWithKeyboard(false);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedLocation) {
      setError('Please search for and select a location.');
      return;
    }

    setSaving(true);
    setError(null);

    if (onDefaultLocationChange) {
      const result = await onDefaultLocationChange(selectedLocation.name, selectedLocation.type);
      if (!result?.allowed) {
        setError('Search limit reached. Upgrade to set your default location.');
        setSaving(false);
        return;
      }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${getApiBaseUrl()}/user/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          default_location_name: selectedLocation.name,
          default_location_type: selectedLocation.type
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      onComplete({
        name: selectedLocation.name,
        type: selectedLocation.type
      });
    } catch (err) {
      console.error('Error saving default location:', err);
      setError('Failed to save your location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getDisplayName = (result) => {
    if ((result.type === 'county' || result.type === 'city') && result.name.includes(', ')) {
      const parts = result.name.split(', ');
      const local = parts.slice(0, -1).join(', ');
      const state = parts[parts.length - 1];
      const stateAbbr = getStateAbbreviation(state.trim());
      return `${local}, ${stateAbbr}`;
    }
    return result.name;
  };

  return (
    <div className="modal-overlay">
      <div className="default-location-modal">
        <div className="modal-header">
          <h2>Set Your Default Location</h2>
        </div>

        <div className="modal-content default-location-content">
          <div className="search-info default-location-info">
            <MapPin size={16} />
            <span>Choose the location that loads when you log in. You can change this anytime in your profile settings.</span>
          </div>

          {error && (
            <div className="search-info default-location-error">
              {error}
            </div>
          )}

          <div className="default-location-search-wrapper" ref={searchRef}>
            <div className="search-container default-location-search">
              <Search className="search-icon" />
              <input
                ref={inputRef}
                type="text"
                className="search-input"
                placeholder="Enter city, county or state"
                value={inputValue}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (inputValue && !selectedLocation) {
                    setShowDropdown(searchResults.length > 0);
                  }
                }}
              />
              {inputValue && (
                <button
                  className="clear-search"
                  style={{ right: '8px' }}
                  onClick={() => {
                    setInputValue('');
                    setSelectedLocation(null);
                    setSearchResults([]);
                    setShowDropdown(false);
                    setHighlightedIndex(-1);
                    setError(null);
                    inputRef.current?.focus();
                  }}
                >
                  <X size={16} />
                </button>
              )}

              {showDropdown && searchResults.length > 0 && (
                <ul
                  className="search-dropdown default-location-dropdown"
                  ref={dropdownRef}
                  data-keyboard-nav={isNavigatingWithKeyboard}
                  style={{ maxHeight: `${dropdownMaxHeight}px` }}
                >
                  {locationTypes.slice().reverse().map(locationType => {
                    const typeResults = searchResults.filter(result => result.type === locationType.value);
                    if (typeResults.length === 0) return null;

                    return (
                      <div key={locationType.value} className="search-dropdown-section">
                        <h6 className="search-dropdown-heading">
                          {locationType.label}
                        </h6>
                        {typeResults.map((result) => {
                          const globalIndex = getFlattenedResults().findIndex(
                            item => item.name === result.name && item.type === result.type
                          );
                          return (
                            <li
                              key={result.name}
                              data-index={globalIndex}
                              className={globalIndex === highlightedIndex ? 'highlighted' : ''}
                              onClick={() => handleLocationSelect(result)}
                              onMouseEnter={() => {
                                setHighlightedIndex(globalIndex);
                                setIsNavigatingWithKeyboard(false);
                              }}
                            >
                              {getDisplayName(result)}
                            </li>
                          );
                        })}
                      </div>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="modal-actions default-location-actions">
          <button
            className="confirm-button"
            onClick={handleConfirm}
            disabled={!selectedLocation || saving}
          >
            {saving ? 'Saving...' : 'Set Default Location'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DefaultLocationModal;